import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import type {
  DotAction,
  DotControl,
  DotHistoryEntry,
  DotSettings,
  DotSummary,
  DotUpdatePayload,
} from "./types";

const DOT_ENDPOINT = END_POINTS.DOT_COMPLIANCE;

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    ax?.response?.data?.message ||
    ax?.response?.data?.error ||
    ax?.message ||
    fallback
  );
}

function normalizeControl(raw: DotControl): DotControl {
  return {
    ...raw,
    id: String(raw.id || ""),
    title: raw.title || "",
    description: raw.description || "",
    note: raw.note || "",
    section: raw.section || "",
    section_heading: raw.section_heading || "",
    subsection: raw.subsection || "",
    subsection_heading: raw.subsection_heading || "",
    status: raw.status ?? null,
    source: raw.source ?? null,
    comments: raw.comments || "",
    updated_at: raw.updated_at ?? null,
    updated_by:
      (typeof raw.updated_by === "string" && raw.updated_by.trim()) ||
      (typeof (raw as { updated_by_email?: string }).updated_by_email ===
        "string" &&
        (raw as { updated_by_email?: string }).updated_by_email!.trim()) ||
      null,
    pid_mapped: Boolean(raw.pid_mapped),
    na: Boolean(raw.na),
    linked_pids: Array.isArray(raw.linked_pids) ? raw.linked_pids : [],
    auto_status: raw.auto_status ?? null,
    override: Boolean(raw.override),
    override_reason: raw.override_reason || "",
  };
}

/**
 * Live DoT / MBSS API — POST securityscan/dot-compliance with body.action.
 * No dummy fallback; callers must handle errors.
 */
export async function callDotCompliance<T = unknown>(
  action: DotAction,
  payload: Record<string, unknown> = {}
): Promise<T> {
  try {
    const response = await axiosInstance.post(DOT_ENDPOINT, {
      action,
      ...payload,
    });
    const body = response?.data;
    if (body && typeof body === "object" && body.success === false) {
      throw new Error(
        body.message || body.error || `DoT action '${action}' failed`
      );
    }
    return (body?.data ?? body) as T;
  } catch (err) {
    throw new Error(apiErrorMessage(err, `DoT action '${action}' failed`));
  }
}

export async function fetchDotControls(): Promise<DotControl[]> {
  const data = await callDotCompliance<{ controls?: DotControl[] }>(
    "list_controls"
  );
  const list = Array.isArray(data)
    ? (data as unknown as DotControl[])
    : data?.controls || [];
  return list.map(normalizeControl);
}

export async function fetchDotSummary(): Promise<DotSummary> {
  const data = await callDotCompliance<DotSummary>("summary_score");
  if (!data || typeof data.total !== "number") {
    throw new Error("Invalid DoT summary response");
  }
  return data;
}

export async function fetchDotSettings(): Promise<DotSettings> {
  const data = await callDotCompliance<DotSettings>("get_settings");
  if (!data || typeof data.stale_days !== "number") {
    throw new Error("Invalid DoT settings response");
  }
  return data;
}

export async function saveDotSettings(
  settings: Partial<DotSettings>
): Promise<DotSettings> {
  const data = await callDotCompliance<DotSettings>("update_settings", settings);
  if (!data || typeof data.stale_days !== "number") {
    throw new Error("Invalid DoT settings update response");
  }
  return data;
}

export async function saveDotControl(
  payload: DotUpdatePayload
): Promise<DotControl> {
  const data = await callDotCompliance<{ control?: DotControl }>(
    "update_control",
    payload as unknown as Record<string, unknown>
  );
  if (!data?.control) {
    throw new Error("Unable to update control");
  }
  return normalizeControl(data.control);
}

export async function fetchDotHistory(): Promise<DotHistoryEntry[]> {
  const data = await callDotCompliance<{ history?: DotHistoryEntry[] }>(
    "history"
  );
  if (Array.isArray(data)) return data as unknown as DotHistoryEntry[];
  return data?.history || [];
}
